import type {
  createOrganizerGroupRequestSchema,
  OrganizerGroup,
  OrganizerGroupMember,
  OrganizerGroupMessage,
  sendOrganizerGroupMessageRequestSchema,
  updateOrganizerGroupRequestSchema,
} from '@pulsify/shared/dto'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { z } from 'zod'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { api, unwrap } from '~/shared/lib/rpc'

const THREAD_REFRESH_MS = 5 * 1000
const LIST_REFRESH_MS = 30 * 1000

type CreateGroup = z.input<typeof createOrganizerGroupRequestSchema>
type UpdateGroup = z.input<typeof updateOrganizerGroupRequestSchema>
type SendMessage = z.input<typeof sendOrganizerGroupMessageRequestSchema>

const groups = () => api.api.businesses[':businessId'].groups
const group = () => groups()[':groupId']

export function useOrganizerGroups(businessId: string, hostProfileId: string | undefined) {
  return useQuery({
    queryKey: queryKey(QueryKey.ORGANIZER_GROUPS, businessId, hostProfileId ?? ''),
    queryFn: async () => {
      const res = await groups().$get({
        param: { businessId },
        query: { hostProfileId: hostProfileId ?? '' },
      })
      return (await unwrap<{ groups: OrganizerGroup[] }>(res)).groups
    },
    enabled: !!businessId && !!hostProfileId,
    refetchInterval: LIST_REFRESH_MS,
  })
}

/** The newest page of a group's messages, kept fresh while it is open. */
export function useOrganizerGroupMessages(businessId: string, groupId: string | null) {
  return useQuery({
    queryKey: queryKey(QueryKey.ORGANIZER_GROUP_MESSAGES, groupId ?? ''),
    queryFn: async () => {
      const res = await group().messages.$get({
        param: { businessId, groupId: groupId ?? '' },
        query: {},
      })
      return (await unwrap<{ messages: OrganizerGroupMessage[] }>(res)).messages
    },
    enabled: !!businessId && !!groupId,
    refetchInterval: THREAD_REFRESH_MS,
  })
}

export function useOrganizerGroupMembers(businessId: string, groupId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKey(QueryKey.ORGANIZER_GROUP_MEMBERS, groupId),
    queryFn: async () => {
      const res = await group().members.$get({ param: { businessId, groupId } })
      return (await unwrap<{ members: OrganizerGroupMember[] }>(res)).members
    },
    enabled: enabled && !!businessId && !!groupId,
  })
}

function useRefreshGroups() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.ORGANIZER_GROUPS) })
}

export function useCreateOrganizerGroup(businessId: string) {
  const refresh = useRefreshGroups()
  return useMutation({
    mutationFn: async (json: CreateGroup) =>
      unwrap<OrganizerGroup>(await groups().$post({ param: { businessId }, json })),
    onSuccess: refresh,
  })
}

export function useUpdateOrganizerGroup(businessId: string, groupId: string) {
  const refresh = useRefreshGroups()
  return useMutation({
    mutationFn: async (json: UpdateGroup) =>
      unwrap<OrganizerGroup>(await group().$patch({ param: { businessId, groupId }, json })),
    onSuccess: refresh,
  })
}

export function useResetOrganizerGroupInvite(businessId: string, groupId: string) {
  const refresh = useRefreshGroups()
  return useMutation({
    mutationFn: async () =>
      unwrap<OrganizerGroup>(
        await group()['invite-link'].reset.$post({ param: { businessId, groupId } }),
      ),
    onSuccess: refresh,
  })
}

function useRefreshThread(groupId: string) {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({
      queryKey: queryKey(QueryKey.ORGANIZER_GROUP_MESSAGES, groupId),
    })
}

export function useSendOrganizerGroupMessage(businessId: string, groupId: string) {
  const refresh = useRefreshThread(groupId)
  return useMutation({
    mutationFn: async (json: SendMessage) =>
      unwrap<OrganizerGroupMessage>(
        await group().messages.$post({ param: { businessId, groupId }, json }),
      ),
    onSuccess: refresh,
  })
}

export function useDeleteOrganizerGroupMessage(businessId: string, groupId: string) {
  const refresh = useRefreshThread(groupId)
  return useMutation({
    mutationFn: async (messageId: string) => {
      const res = await group().messages[':messageId'].$delete({
        param: { businessId, groupId, messageId },
      })
      await unwrap<void>(res)
    },
    onSuccess: refresh,
  })
}

export function useRemoveOrganizerGroupMember(businessId: string, groupId: string) {
  const queryClient = useQueryClient()
  const refresh = useRefreshGroups()
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await group().members[':userId'].$delete({
        param: { businessId, groupId, userId },
      })
      await unwrap<void>(res)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.ORGANIZER_GROUP_MEMBERS, groupId),
      })
      return refresh()
    },
  })
}
